import React from "react";
import { FileText, ArrowUpRight } from "lucide-react";
import { REPORT } from "@/data/content";
import { Reveal } from "@/lib/reveal";

// The Report / The Data: clean, typographic editorial spread introducing the
// standalone, freely available Australia Regenerative Agricultural Report.
export const TheReport = () => {
  return (
    <section id="report" data-testid="the-report" className="relative overflow-hidden bg-paper py-24 md:py-32">
      <div className="mx-auto grid max-w-[1400px] items-start gap-14 px-5 md:px-10 lg:grid-cols-12 lg:gap-20">
        {/* Left: the case for the report */}
        <div className="lg:col-span-5">
          <Reveal>
            <span className="inline-flex items-center gap-2 font-body text-xs uppercase tracking-[0.3em] text-terracotta">
              <FileText size={14} /> {REPORT.eyebrow}
            </span>
            <h2 className="mt-5 font-display text-4xl font-medium leading-[1.02] text-ink sm:text-5xl lg:text-[3.4rem]">
              {REPORT.heading}
            </h2>
            <p className="mt-2 font-script text-3xl text-sage md:text-4xl">{REPORT.headingTail}</p>

            <p className="mt-7 max-w-lg font-body text-base leading-relaxed text-ink/80 md:text-lg">
              {REPORT.lead}
            </p>
            <p className="mt-4 max-w-lg font-body text-base leading-relaxed text-ink/65">
              {REPORT.body}
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-10 grid grid-cols-3 gap-4 border-t border-clay pt-8">
              {REPORT.stats.map((s) => (
                <div key={s.label} data-testid={`report-stat-${s.value}`}>
                  <div className="font-display text-3xl font-medium text-forest md:text-4xl">{s.value}</div>
                  <div className="mt-1 font-body text-xs leading-snug text-ink/60">{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Right: the contents, styled like a report table of contents */}
        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <div className="rounded-2xl border border-clay bg-cream p-6 shadow-[0_28px_70px_rgba(42,32,21,0.12)] md:p-10">
              <div className="flex items-baseline justify-between border-b border-clay pb-4">
                <span className="font-body text-xs uppercase tracking-[0.3em] text-carbondark">Contents</span>
                <span className="font-body text-xs uppercase tracking-[0.2em] text-sage">24 pages · fully sourced</span>
              </div>

              <ol className="mt-2 divide-y divide-clay/70">
                {REPORT.contents.map((c) => (
                  <li
                    key={c.n}
                    data-testid={`report-section-${c.n}`}
                    className="group flex items-center gap-4 py-3.5 transition-colors hover:bg-paper/70"
                  >
                    <span className="font-display text-sm text-ochre">{c.n}</span>
                    <span className="flex-1 font-display text-lg leading-tight text-ink transition-colors group-hover:text-forest md:text-xl">
                      {c.title}
                    </span>
                    <span className="font-body text-xs uppercase tracking-wider text-ink/45">{c.pages}pp</span>
                  </li>
                ))}
              </ol>

              <div className="mt-6 flex items-start gap-3 rounded-xl bg-forest/5 p-4">
                <ArrowUpRight size={18} className="mt-0.5 shrink-0 text-forest" />
                <p className="font-body text-sm leading-relaxed text-ink/70">{REPORT.note}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
